// Phase 2.11 Tier 2 — public warranty claim form file uploader.
//
// Mirrors the chip pattern from `report_form_controller.js` but the
// "register" step happens server-side on form submit (the controller
// looks up the cache_name list and creates Attachment rows tied to the
// new WarrantyClaim). For the public form we just need to:
//   1. Presign URLs via POST to the controller's presign endpoint
//   2. PUT each file to S3
//   3. Maintain the list of registered attachment IDs in the hidden
//      `attachment_ids` input so the create POST binds them.

import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["fileInput", "uploadList", "attachmentIds"]
  static values = { presignUrl: String }

  connect() {
    this._registeredIds = []
  }

  openFilePicker() {
    this.fileInputTarget?.click()
  }

  async filesSelected(event) {
    const files = Array.from(event.target.files || [])
    for (const file of files) {
      await this._uploadFile(file)
    }
    event.target.value = ""
  }

  async _uploadFile(file) {
    const csrf = document.querySelector('meta[name="csrf-token"]')?.content
    const chip = this._createChip(file.name, "uploading")
    this.uploadListTarget?.appendChild(chip)

    try {
      const presignRes = await fetch(this.presignUrlValue, {
        method: "POST",
        headers: { "X-CSRF-Token": csrf, "Content-Type": "application/json" },
        body: JSON.stringify({
          files: [{
            filename: file.name,
            content_type: file.type || "application/octet-stream",
            byte_size: file.size
          }]
        })
      })
      if (!presignRes.ok) throw new Error(`presign HTTP ${presignRes.status}`)
      const { uploads } = await presignRes.json()
      const upload = uploads[0]
      if (!upload) throw new Error("Empty presign response")

      const putRes = await fetch(upload.upload_url, {
        method: "PUT",
        headers: { "Content-Type": file.type || upload.content_type },
        body: file
      })
      if (!putRes.ok) throw new Error(`S3 PUT failed (${putRes.status})`)

      // The public form binds attachments at submit time (one atomic
      // create) — embed the cache_name list in the hidden field so the
      // server's create action can iterate and turn each into an
      // Attachment row tied to the new claim.
      const cacheNames = (this.attachmentIdsTarget.value || "").split(",").filter(Boolean)
      cacheNames.push(upload.cache_name)
      this.attachmentIdsTarget.value = cacheNames.join(",")

      this._setChipDone(chip, file.name)
    } catch (e) {
      console.error("[public-warranty-claim] upload failed:", e)
      this._setChipFailed(chip)
    }
  }

  _createChip(name, state) {
    const chip = document.createElement("div")
    chip.className = "inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-xs"
    chip.classList.add(state === "uploading" ? "chip-uploading" : "chip-file")
    const truncated = name.length > 28 ? name.slice(0, 24) + "..." + name.slice(name.lastIndexOf(".")) : name
    chip.innerHTML = `
      <svg class="w-3.5 h-3.5 shrink-0 animate-spin" fill="none" viewBox="0 0 24 24">
        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
      </svg>
      <span class="truncate max-w-[180px]" title="${name}">${truncated}</span>
    `
    return chip
  }

  _setChipDone(chip, name) {
    chip.classList.remove("chip-uploading")
    chip.classList.add("chip-file")
    const truncated = name.length > 28 ? name.slice(0, 24) + "..." + name.slice(name.lastIndexOf(".")) : name
    chip.innerHTML = `
      <svg class="w-3.5 h-3.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
        <path stroke-linecap="round" stroke-linejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z"/>
      </svg>
      <span class="truncate max-w-[180px]" title="${name}">${truncated}</span>
    `
  }

  _setChipFailed(chip) {
    chip.classList.remove("chip-uploading")
    chip.classList.add("bg-rose-50", "text-rose-700")
    chip.innerHTML = `<span>Upload failed — try a smaller file.</span>`
  }
}
